import {
  checkIsMastraErrorToolOutput,
  type MastraErrorToolOutput,
} from "@repo/app-types/agents/mastraErrorToolOutput";
import type {
  TestSignalProposalToolInput,
  TestSignalProposalToolOutput,
} from "@repo/app-types/entities/configurationAgent";
import { renderPlural } from "@repo/app-types/helpers/utilities";
import { ChatToolBase, type ToolComponent } from "@repo/ui/chat/chatToolCall";
import { useEffect, useRef } from "react";
import { useConfigurationAssistantContext } from "../configurationAssistant/configurationAssistantContext.js";

export const TestSignalProposalTool: ToolComponent<
  TestSignalProposalToolInput,
  TestSignalProposalToolOutput | MastraErrorToolOutput
> = (props) => {
  const { onTestResultsLoading, onTestResultsComplete } =
    useConfigurationAssistantContext();
  const reportedLoadingRef = useRef(false);
  const reportedCompleteRef = useRef(false);

  // Notify the parent (signal editor) of test tool status changes:
  // - "loading" when the tool starts running (show loading state in results pane)
  // - "complete" when results arrive successfully (show results in pane)
  useEffect(() => {
    if (
      props.status.type === "running" &&
      onTestResultsLoading &&
      !reportedLoadingRef.current
    ) {
      reportedLoadingRef.current = true;
      onTestResultsLoading();
    }

    if (
      props.status.type === "complete" &&
      props.output &&
      !checkIsMastraErrorToolOutput(props.output) &&
      onTestResultsComplete &&
      !reportedCompleteRef.current
    ) {
      reportedCompleteRef.current = true;
      onTestResultsComplete(props.output);
    }
  }, [
    props.status.type,
    props.output,
    onTestResultsLoading,
    onTestResultsComplete,
  ]);

  if (props.status.type === "running") {
    return (
      <ChatToolBase status={props.status}>Testing signal proposal</ChatToolBase>
    );
  }

  if (checkIsMastraErrorToolOutput(props.output)) {
    return (
      <ChatToolBase status={props.status}>
        An error occurred while testing the signal proposal
      </ChatToolBase>
    );
  }

  if (props.status.type === "complete" && props.output) {
    const { summary } = props.output;
    return (
      <ChatToolBase status={props.status}>
        Tested on{" "}
        {renderPlural(summary.total_interactions, {
          one: "1 interaction",
          other: (n) => `${n} interactions`,
        })}{" "}
        &mdash;{" "}
        {renderPlural(summary.total_matches, {
          one: "1 match",
          other: (n) => `${n} matches`,
        })}{" "}
        found
      </ChatToolBase>
    );
  }

  return (
    <ChatToolBase status={props.status}>
      Testing signal proposal...
    </ChatToolBase>
  );
};
